import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Select,
    Option,
    IconButton,
    Chip,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from "@material-tailwind/react";
import {
    PlusIcon,
    TrashIcon,
    ExclamationCircleIcon,
    CheckCircleIcon
} from "@heroicons/react/24/outline";
import ConfirmModal from '../../common/ConfirmModal';

const TaskDependencySection = ({ taskId, projectId }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');
    const [userId] = useState(localStorage.getItem('userId') || '');

    const [dependencies, setDependencies] = useState({ blocking: [], blocked_by: [] });
    const [availableTasks, setAvailableTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState('');
    const [dependencyType, setDependencyType] = useState('blocks');
    const [canComplete, setCanComplete] = useState(true);
    const [deleteDependencyId, setDeleteDependencyId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        fetchDependencies();
        fetchAvailableTasks();
        checkCanComplete();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId]);

    const fetchDependencies = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/tasks/${taskId}/dependencies`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setDependencies(response.data.data || { blocking: [], blocked_by: [] });
            }
        } catch (error) {
            console.error('Error fetching dependencies:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTasks = async () => {
        if (!projectId) return;

        try {
            const response = await axios.get(`${rootUrl}/api/tasks/project/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                // Filter out current task
                const tasks = response.data.data.filter(t => t.id !== taskId);
                setAvailableTasks(tasks);
            }
        } catch (error) {
            console.error('Error fetching available tasks:', error);
        }
    };

    const checkCanComplete = async () => {
        try {
            const response = await axios.get(`${rootUrl}/api/tasks/${taskId}/can-complete`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCanComplete(Boolean(response.data.data.canComplete ?? response.data.data.can_complete));
            }
        } catch (error) {
            console.error('Error checking completion status:', error);
        }
    };

    const handleAddDependency = async () => {
        if (!selectedTaskId) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/dependencies`,
                    {
                        depends_on_task_id: selectedTaskId,
                        dependency_type: dependencyType,
                    created_by: userId
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setShowAddDialog(false);
                setSelectedTaskId('');
                setDependencyType('blocks');
                fetchDependencies();
                checkCanComplete();
            }
        } catch (error) {
            console.error('Error adding dependency:', error);
            alert(error.response?.data?.message || 'Failed to add dependency');
        }
    };

    const handleRemoveDependency = async () => {
        if (!deleteDependencyId) return;

        setDeleteLoading(true);
        setDeleteError('');

        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/dependencies/${deleteDependencyId}?deleted_by=${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                fetchDependencies();
                checkCanComplete();
                setDeleteDependencyId(null);
            } else {
                setDeleteError(response.data.message || 'Failed to remove dependency');
            }
        } catch (error) {
            console.error('Error removing dependency:', error);
            setDeleteError(error?.response?.data?.message || 'Failed to remove dependency');
        } finally {
            setDeleteLoading(false);
        }
    };

    const getDependencyTypeLabel = (type) => {
        const labels = {
            'blocks': 'Blocks',
            'blocking': 'Blocks',
            'blocked_by': 'Blocked By',
            'relates_to': 'Relates To'
        };
        return labels[type] || type;
    };

    const getStatusColor = (status) => {
        const colors = {
            'todo': 'gray',
            'in_progress': 'blue',
            'in_review': 'purple',
            'completed': 'green',
            'blocked': 'red'
        };
        return colors[status] || 'gray';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'low': 'blue',
            'medium': 'orange',
            'high': 'red',
            'urgent': 'deep-orange'
        };
        return colors[priority] || 'gray';
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Typography>Loading dependencies...</Typography>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with Status */}
            <div className="flex justify-between items-start">
                <div>
                    <Typography variant="h5" color="blue-gray">
                        Dependencies
                    </Typography>
                    {!canComplete && (
                        <div className="flex items-center gap-2 mt-2 text-red-500">
                            <ExclamationCircleIcon className="h-5 w-5" />
                            <Typography variant="small">
                                This task cannot be completed until dependencies are resolved
                            </Typography>
                        </div>
                    )}
                    {canComplete && dependencies.blocked_by.length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-green-500">
                            <CheckCircleIcon className="h-5 w-5" />
                            <Typography variant="small">
                                All dependencies are satisfied
                            </Typography>
                        </div>
                    )}
                </div>
                <Button
                    size="sm"
                    className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#E04E2E]"
                    onClick={() => setShowAddDialog(true)}
                >
                    <PlusIcon className="h-4 w-4" />
                    Add Dependency
                </Button>
            </div>

            {/* Blocked By Section */}
            {dependencies.blocked_by.length > 0 && (
                <div>
                    <Typography variant="h6" color="blue-gray" className="mb-3">
                        Blocked By ({dependencies.blocked_by.length})
                    </Typography>
                    <div className="space-y-2">
                        {dependencies.blocked_by.map((dep) => (
                            <Card key={dep.dependency_id} className="border border-gray-200">
                                <CardBody className="p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Typography variant="h6" color="blue-gray">
                                                    {dep.depends_on_task_title}
                                                </Typography>
                                                <Chip
                                                    value={getDependencyTypeLabel(dep.dependency_type)}
                                                    size="sm"
                                                    className="bg-red-100 text-red-700"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Chip
                                                    value={dep.depends_on_task_status}
                                                    size="sm"
                                                    color={getStatusColor(dep.depends_on_task_status)}
                                                />
                                                <Chip
                                                    value={dep.depends_on_task_priority}
                                                    size="sm"
                                                    color={getPriorityColor(dep.depends_on_task_priority)}
                                                />
                                            </div>
                                            {dep.depends_on_task_description && (
                                                <Typography variant="small" color="gray" className="mt-2">
                                                    {dep.depends_on_task_description}
                                                </Typography>
                                            )}
                                        </div>
                                        <IconButton
                                            variant="text"
                                            size="sm"
                                            color="red"
                                            onClick={() => setDeleteDependencyId(dep.dependency_id)}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </IconButton>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Blocking Section */}
            {dependencies.blocking.length > 0 && (
                <div>
                    <Typography variant="h6" color="blue-gray" className="mb-3">
                        Blocking ({dependencies.blocking.length})
                    </Typography>
                    <div className="space-y-2">
                        {dependencies.blocking.map((dep) => (
                            <Card key={dep.dependency_id} className="border border-gray-200">
                                <CardBody className="p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Typography variant="h6" color="blue-gray">
                                                    {dep.task_title}
                                                </Typography>
                                                <Chip
                                                    value={getDependencyTypeLabel(dep.dependency_type)}
                                                    size="sm"
                                                    className="bg-orange-100 text-orange-700"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Chip
                                                    value={dep.task_status}
                                                    size="sm"
                                                    color={getStatusColor(dep.task_status)}
                                                />
                                                <Chip
                                                    value={dep.task_priority}
                                                    size="sm"
                                                    color={getPriorityColor(dep.task_priority)}
                                                />
                                            </div>
                                            {dep.task_description && (
                                                <Typography variant="small" color="gray" className="mt-2">
                                                    {dep.task_description}
                                                </Typography>
                                            )}
                                        </div>
                                        <IconButton
                                            variant="text"
                                            size="sm"
                                            color="red"
                                            onClick={() => setDeleteDependencyId(dep.dependency_id)}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </IconButton>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {dependencies.blocked_by.length === 0 && dependencies.blocking.length === 0 && (
                <Card className="bg-gray-50">
                    <CardBody className="text-center py-8">
                        <Typography color="gray">
                            No dependencies yet. Add dependencies to manage task relationships.
                        </Typography>
                    </CardBody>
                </Card>
            )}

            {/* Add Dependency Dialog */}
            <Dialog
                open={showAddDialog}
                handler={() => setShowAddDialog(false)}
                size="md"
            >
                <DialogHeader>Add Task Dependency</DialogHeader>
                <DialogBody className="space-y-4">
                    <div>
                        <Typography variant="small" color="gray" className="mb-2">
                            Dependency Type
                        </Typography>
                        <Select
                            value={dependencyType}
                            onChange={(value) => setDependencyType(value)}
                            className="!border-[#FF5733] focus:!border-[#FF5733]"
                        >
                            <Option value="blocks">This task blocks...</Option>
                            <Option value="blocked_by">This task is blocked by...</Option>
                            <Option value="relates_to">This task relates to...</Option>
                        </Select>
                    </div>

                    <div>
                        <Typography variant="small" color="gray" className="mb-2">
                            Select Task
                        </Typography>
                        <Select
                            value={selectedTaskId}
                            onChange={(value) => setSelectedTaskId(value)}
                            className="!border-[#FF5733] focus:!border-[#FF5733]"
                        >
                            {availableTasks.map((task) => (
                                <Option key={task.id} value={task.id}>
                                    {task.title} ({task.status})
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg">
                        <Typography variant="small" color="blue-gray">
                            <strong>Note:</strong> Dependencies help manage task relationships and completion order.
                        </Typography>
                    </div>
                </DialogBody>
                <DialogFooter className="space-x-2">
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowAddDialog(false);
                            setSelectedTaskId('');
                            setDependencyType('blocks');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddDependency}
                        disabled={!selectedTaskId}
                        className="bg-[#FF5733] hover:bg-[#E04E2E]"
                    >
                        Add Dependency
                    </Button>
                </DialogFooter>
            </Dialog>

            <ConfirmModal
                isOpen={Boolean(deleteDependencyId)}
                title="Remove dependency"
                description="This dependency link will be removed from the task."
                onCancel={() => {
                    setDeleteDependencyId(null);
                    setDeleteError('');
                }}
                onConfirm={handleRemoveDependency}
                confirmText="Remove"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
};

export default TaskDependencySection;
